import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWidget, MetricCard } from './BaseWidget'
import { API_BASE_URL } from '../../utils/api'
import { DesignTheme } from './FinancialWidgetDesignSelector'

interface LeadOverviewWidgetProps {
  className?: string
  designTheme?: DesignTheme
  widgetId?: string
}

interface LeadMetrics {
  totalLeads: number
  newLeads: number
  followUpsDue: number
  convertedThisWeek: number
  conversionRate: number
  averageResponseTime: number
  hotLeads: number
  byStatus: Array<{
    status_name: string
    status_color: string
    count: number
  }>
  recentActivity: Array<{
    id: number
    name: string
    action: string
    timestamp: string
  }>
}

// Icon components
const LeadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
)

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const LeadOverviewWidget: React.FC<LeadOverviewWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [metrics, setMetrics] = useState<LeadMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMinimal, setIsMinimal] = useState(false)

  // Detect widget height for minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'lead_overview'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        // Trigger minimal display at 340px or less (h:2 grid units)
        setIsMinimal(height <= 340)
      }
    }
    
    checkHeight()
    // Add a small delay to ensure proper rendering
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'lead_overview'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  // Function to translate status names
  const translateStatusName = (statusName: string): string => {
    const statusMap: { [key: string]: string } = {
      'Waiting': t('dashboard.widgets.statusWaiting'),
      'New': t('dashboard.widgets.statusNew'),
      'Called': t('dashboard.widgets.statusCalled'),
      'waiting': t('dashboard.widgets.statusWaiting'),
      'new': t('dashboard.widgets.statusNew'),
      'called': t('dashboard.widgets.statusCalled'),
    }
    return statusMap[statusName] || statusName
  }

  const fetchLeadMetrics = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setError('No authentication token')
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/dashboard/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: LeadMetrics = await response.json()
      setMetrics(data)
      setError(null)
    } catch (error) {
      console.error('Error fetching lead metrics:', error)
      setError('Failed to load lead metrics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLeadMetrics()
  }, [])

  if (error) {
    return (
      <BaseWidget
        title={t('dashboard.widgets.leadOverview')}
        icon={<LeadIcon className="h-5 w-5" />}
        accent="analytics"
        variant="gradient"
        className={className}
        loading={false}
      >
        <div className="text-center py-4">
          <p className="text-red-500 text-sm">{error}</p>
          <button 
            onClick={fetchLeadMetrics}
            className="mt-2 px-3 py-1 bg-gray-800 text-white rounded text-xs hover:bg-gray-900"
          >
            {t('dashboard.widgets.retry')}
          </button>
        </div>
      </BaseWidget>
    )
  }

  return (
    <BaseWidget
      title={t('dashboard.widgets.leadOverview')}
      icon={<LeadIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !metrics}
      minimalDisplay={isMinimal}
    >
      {isMinimal && metrics ? (
        // Minimal display - clean 2x2 grid with balanced text sizes
        <div className="grid grid-cols-2 gap-2 h-full items-center px-2 py-1">
          <div className="text-center bg-white/10 rounded-lg py-2 px-1">
            <div className="text-3xl font-bold text-white tabular-nums">{metrics.newLeads || 0}</div>
            <div className="text-xs text-white/90 mt-0.5 font-medium uppercase tracking-wider">{t('dashboard.widgets.newLeads')}</div>
          </div>
          <div className="text-center bg-white/10 rounded-lg py-2 px-1">
            <div className="text-3xl font-bold text-white tabular-nums">{metrics.followUpsDue || 0}</div>
            <div className="text-xs text-white/90 mt-0.5 font-medium uppercase tracking-wider">{t('dashboard.widgets.followUpsDue')}</div>
          </div>
          <div className="text-center bg-white/10 rounded-lg py-2 px-1">
            <div className="text-3xl font-bold text-white tabular-nums">{metrics.hotLeads || 0}</div>
            <div className="text-xs text-white/90 mt-0.5 font-medium uppercase tracking-wider">{t('dashboard.widgets.hotLeads')}</div>
          </div>
          <div className="text-center bg-white/10 rounded-lg py-2 px-1">
            <div className="text-3xl font-bold text-white tabular-nums">{(metrics.conversionRate || 0).toFixed(1)}%</div>
            <div className="text-xs text-white/90 mt-0.5 font-medium uppercase tracking-wider">{t('dashboard.widgets.conversionRate')}</div>
          </div>
        </div>
      ) : metrics ? (
        // Full display
        <div className="space-y-3">
          {/* Top Metrics Row */}
          <div className="grid grid-cols-2 gap-2">
            <MetricCard
              label={t('dashboard.widgets.newLeads')}
              value={metrics.newLeads || 0}
              subValue={t('dashboard.widgets.thisWeek')}
              variant="gradient"
              className="p-2"
            />
            <MetricCard
              label={t('dashboard.widgets.followUpsDue')}
              value={metrics.followUpsDue || 0}
              subValue={t('dashboard.widgets.today')}
              variant="gradient"
              className="p-2"
            />
          </div>

          {/* Middle Metrics Row */}
          <div className="grid grid-cols-2 gap-2">
            <MetricCard
              label={t('dashboard.widgets.hotLeads')}
              value={metrics.hotLeads || 0}
              variant="gradient"
              className="p-2"
            />
            <MetricCard
              label={t('dashboard.widgets.conversionRate')}
              value={`${(metrics.conversionRate || 0).toFixed(1)}%`}
              variant="gradient"
              className="p-2"
            />
          </div>

          {/* Status Breakdown */}
          {metrics.byStatus && metrics.byStatus.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-medium text-white/70 mb-2">
                {t('dashboard.widgets.byStatus')}
              </h4>
              <div className="space-y-1">
                {metrics.byStatus.slice(0, 3).map((status, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: status.status_color }}
                      />
                      <span className="text-xs text-white/80">
                        {translateStatusName(status.status_name)}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-white">
                      {status.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </BaseWidget>
  )
}

export default LeadOverviewWidget
